import { deleteRequest, get, patch, postFormData, postJson } from './apiClient';
import type { ConfirmedOrderHoldDto } from './confirmedOrderApi';

export type InboxCategory =
  | 'inbox'
  | 'rfq'
  | 'quotation'
  | 'price_list'
  | 'confirmed_order'
  | 'others';

/**
 * Where a conversation can be moved. Never 'inbox' (D43.11): "moving to Inbox" wiped the
 * classification, which listed the email under Quotations, Price List and Confirmed Orders at
 * once and parked its RFQ. The server refuses it (INBOX_CATEGORY_NOT_ALLOWED).
 */
export type ReclassifyCategory = Exclude<InboxCategory, 'inbox'>;

export interface InboxCounts {
  inbox: number;
  rfq: number;
  quotation: number;
  price_list: number;
  confirmed_order: number;
  others: number;
}

export interface InboxEmailListItem {
  /**
   * The id of the conversation's MOST RECENT message. Still the row's identity for opening the
   * detail pane and for every /inbox/email/:id call, exactly as before threading — which is
   * why the list stayed a drop-in replacement when the server started grouping.
   */
  _id: string;
  /**
   * The conversation this row stands for. Optional only for the transition: a message ingested
   * before the threading migration ran has no threadId, and the UI must degrade to the single
   * message rather than render a broken row.
   */
  threadId?: string;
  /** How many messages are in the conversation. 1 means there is nothing to expand. */
  messageCount?: number;
  /** True once any message in the thread was sent by us. Nothing sets it yet — see below. */
  hasOutbound?: boolean;
  subject: string;
  from: string;
  to?: string;
  receivedAt?: string;
  inboxTypes?: string[];
  inboxLabel?: string;
  hasAttachments?: boolean;
  attachmentCount?: number;
  aiInboxConfidence?: number | null;
  aiInboxClassifiedAt?: string | null;
  aiInboxLowConfidence?: boolean;
}

/** One message inside a conversation, as returned by GET /inbox/thread/:threadId. */
export interface InboxThreadMessage {
  _id: string;
  threadId?: string;
  /**
   * 'outbound' is mail we sent. The server never writes it today — USC has no reply feature and
   * no Sent-folder poll — so every message currently reads 'inbound'. The UI handles both so
   * that whenever outbound logging lands, our own messages appear in the conversation without
   * a frontend change.
   */
  direction?: 'inbound' | 'outbound';
  subject?: string;
  from?: string;
  to?: string;
  cc?: string;
  date?: string;
  receivedAt?: string;
  bodyText?: string;
  bodyHtml?: string;
  attachments?: Array<{
    filename: string;
    mimeType?: string;
    size?: number;
    storagePath?: string;
    fileUrl?: string | null;
  }>;
}

export interface InboxThreadResponse {
  threadId: string;
  messages: InboxThreadMessage[];
}

export interface InboxEmailDetail {
  _id: string;
  subject: string;
  from?: string;
  to?: string;
  cc?: string;
  date?: string;
  receivedAt?: string;
  bodyText?: string;
  bodyHtml?: string;
  rawBodyText?: string;
  rawBodyHtml?: string;
  inboxTypes?: string[];
  inboxLabel?: string;
  aiInboxConfidence?: number | null;
  aiInboxClassifiedAt?: string | null;
  aiInboxReasoning?: string;
  aiInboxLowConfidence?: boolean;
  attachments?: Array<{
    filename: string;
    mimeType?: string;
    size?: number;
    storagePath?: string;
    fileUrl?: string | null;
  }>;
  /** D45.7: an email confirmation that waits for a person (confirmedOrderApi.ts). */
  confirmedOrderHold?: ConfirmedOrderHoldDto | null;
}

export interface InboxListResponse {
  data: InboxEmailListItem[];
  total: number;
  page: number;
  limit: number;
  category: InboxCategory;
}

export async function fetchInboxCounts(): Promise<InboxCounts> {
  const res = await get<{ success: true; data: { counts: InboxCounts } }>('/inbox/counts');
  return res.data.counts;
}

export async function fetchInboxEmails(
  category: InboxCategory,
  page = 1,
  limit = 25,
  search?: string
) {
  const params = new URLSearchParams();
  params.set('category', category);
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (search && search.trim()) params.set('search', search.trim());
  return get<InboxListResponse>(`/inbox?${params.toString()}`);
}

export async function fetchInboxEmailDetail(id: string) {
  return get<InboxEmailDetail>(`/inbox/email/${id}`);
}

/** Every message in one conversation, oldest first. */
export async function fetchInboxThread(threadId: string) {
  return get<InboxThreadResponse>(`/inbox/thread/${encodeURIComponent(threadId)}`);
}

export interface InboxReplyPayload {
  /** Comma-separated addresses. The only required field. */
  to: string;
  cc?: string;
  /** Omitted when blank so the server applies its own `Re: <original subject>` default. */
  subject?: string;
  text?: string;
  attachments?: File[];
}

/**
 * Send a reply to one inbound message. Resolves with the logged OUTBOUND email — the same
 * shape `fetchInboxEmailDetail` returns, which is why `direction` on InboxThreadMessage has
 * always allowed 'outbound'.
 *
 * multipart, so it goes through `postFormData` — the one helper that knows the base URL and
 * the auth header. A failure throws with `.message` and the envelope's `.code`
 * (e.g. EMAIL_SEND_FAILED) already attached by apiClient's handleResponse.
 */
export async function replyToInboxEmail(id: string, payload: InboxReplyPayload) {
  const form = new FormData();
  form.append('to', payload.to);
  if (payload.cc) form.append('cc', payload.cc);
  if (payload.subject) form.append('subject', payload.subject);
  if (payload.text) form.append('text', payload.text);
  (payload.attachments || []).forEach((file) => form.append('attachments', file));

  return postFormData<InboxEmailDetail>(`/inbox/email/${id}/reply`, form);
}

// The four calls below went through hand-rolled fetches that threw the raw response body, so a
// refusal reached the user as `{"success":false,"message":…}` in an alert. They go through
// apiClient now: one base URL, one auth header, and a thrown error carrying the server's `code`.

export async function classifyExistingInboxEmails(payload: {
  scope?: 'missing' | 'all';
  limit?: number;
  dryRun?: boolean;
  afterId?: string | null;
  force?: boolean;
}) {
  return postJson<any>('/inbox/classify', payload || {});
}

export interface InboxReclassifyResult extends InboxEmailDetail {
  /** What moved: every message the client sent in the conversation (D43.11). */
  reclassified?: {
    /** Null for a message stored before threading, which moves alone. */
    threadId: string | null;
    category: ReclassifyCategory;
    /** How many of the client's messages were moved. */
    moved: number;
  };
  /**
   * Present when the move was to RFQ and an RFQ had to be built. `created: 0` with
   * INBOX_RFQ_NO_ATTACHMENT means nothing in the conversation could be read as a NEW RFQ (no
   * request in it, or its file is already an RFQ) — an answer, not a failure: the conversation is
   * still in RFQ.
   */
  rfqCreation?: { created: number; code?: 'INBOX_RFQ_NO_ATTACHMENT' };
}

/**
 * Move the CONVERSATION the message `id` belongs to. Any message of it will do — the list row's
 * `_id`, which is our own reply once we have answered, included: the server resolves the thread
 * and moves every message the client sent in it. A failure throws with `.code` (see
 * `inboxReclassifyErrorMessage`).
 */
export async function updateInboxEmailCategory(id: string, category: ReclassifyCategory) {
  return patch<InboxReclassifyResult>(`/inbox/email/${encodeURIComponent(id)}/category`, { category });
}

/**
 * What to tell the user when a reclassify fails, chosen by the envelope's `code` and nothing
 * else (invariant 6) — the server's prose never reaches the screen from here.
 */
export function inboxReclassifyErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    case 'INBOX_RFQ_NOT_CREATED':
      return 'The conversation was moved to RFQ, but its RFQ could not be created. Move it to RFQ again to retry.';
    case 'INBOX_CATEGORY_NOT_ALLOWED':
    case 'INBOX_CATEGORY_INVALID':
    case 'INBOX_CATEGORY_REQUIRED':
      return 'Choose RFQ, Quotations, Price List, Confirmed Orders or Others.';
    case 'INBOX_NO_CLIENT_EMAIL':
      return 'This conversation holds only emails we sent, so there is nothing to move.';
    case 'EMAIL_NOT_FOUND':
      return 'This email no longer exists. Reload the inbox.';
    default:
      return 'The conversation could not be moved. Reload the inbox to see where it is, then try again — contact support if it keeps failing.';
  }
}

export async function deleteInboxEmail(id: string) {
  return deleteRequest<{ success: boolean }>(`/inbox/email/${encodeURIComponent(id)}`);
}

export async function classifyInboxEmail(id: string) {
  return postJson<InboxEmailDetail>(`/inbox/email/${encodeURIComponent(id)}/classify`, {});
}
